import imageCompression from 'browser-image-compression';
import { formatDistanceToNow } from 'date-fns';
import { ko } from 'date-fns/locale';
import { endSession } from './authSession';
import { colors } from './styles';
import { Intent } from './types';

/* compressImage */ 

export const compressImage = async (file: File, maxWidth = 1200, quality = 0.7): Promise<Blob> => {

    ///////////////////////////////////////////////////////////////////////// 

    const options = {
        maxWidthOrHeight: maxWidth,
        initialQuality: quality, // 압축 품질 (0.0 ~ 1.0)
        useWebWorker: true,
    };

    ///////////////////////////////////////////////////////////////////////// 

    try {
        const compressedBlob = await imageCompression(file, options);
        return compressedBlob;
    } catch (error) {
        console.error('이미지 압축 실패:', error);
        throw error;
    }
};

/* forceLogout */

export const forceLogout = () => {
    void endSession({
        revokeSession: async (refreshToken) => {
            const { logoutSession } = await import("./api");
            await logoutSession(refreshToken);
        },
    });
};

/* api error */

interface IApiErrorBody {
    code?: string;
    message?: string;
    statusCode?: number;
}

const commonApiErrorMessages: Record<string, string> = {
    VALIDATION_ERROR: "입력한 정보를 다시 확인해주세요.",
    UNAUTHORIZED: "로그인이 필요합니다. 다시 로그인해주세요.",
    INVALID_TOKEN: "로그인 정보가 만료되었습니다. 다시 로그인해주세요.",
    INVALID_REFRESH_TOKEN: "로그인 정보가 만료되었습니다. 다시 로그인해주세요.",
    ADMIN_NOT_FOUND: "관리자 정보를 찾을 수 없습니다.",
    PHARMACY_NOT_FOUND: "약국 정보를 찾을 수 없습니다.",
    MEDICINE_NOT_FOUND: "의약품 정보를 찾을 수 없습니다.",
    DUPLICATE_MEDICINE: "이미 등록된 의약품입니다.",
    INTERNAL_SERVER_ERROR: "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.",
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === "object" && value !== null;
};

export const getApiErrorBody = (error: unknown): IApiErrorBody => {
    const response = isRecord(error) ? error.response : undefined;
    const data = isRecord(response) ? response.data : undefined;

    if (!isRecord(data)) return {};

    return {
        code: typeof data.code === "string" ? data.code : undefined,
        message: typeof data.message === "string" ? data.message : undefined,
        statusCode: typeof data.statusCode === "number" ? data.statusCode : undefined,
    };
};

export const getApiErrorCode = (error: unknown) => {
    return getApiErrorBody(error).code;
};

export const getApiErrorMessage = (
    error: unknown,
    fallback: string
) => {

    const {
        code,
        message: apiMessage,
    } = getApiErrorBody(error);

    ////////////////////////////////////////////////////////////
    // 프론트에서 정의한 공통 메시지 우선

    if (
        code &&
        commonApiErrorMessages[code]
    ) {
        return commonApiErrorMessages[code];
    }

    ////////////////////////////////////////////////////////////
    // 별도 정의가 없으면 서버 메시지

    if (apiMessage) {
        return apiMessage;
    }

    ////////////////////////////////////////////////////////////
    // 일반 Error 메시지

    if (
        isRecord(error) &&
        typeof error.message === "string"
    ) {
        return error.message;
    }

    ////////////////////////////////////////////////////////////
    // 최종 fallback

    return fallback;
};

/* formatPrice */ 

export const formatPrice = (price: string | null | undefined): string => {

    ///////////////////////////////////////////////////////////////////////// 가격 정보 없음 처리

    if (price === "" || price === null || price === undefined) {
        return "가격 정보 미등록";
    }

    const priceToNumber = Number(price);

    ///////////////////////////////////////////////////////////////////////// 0 미만의 숫자 예외 처리

    if (priceToNumber < 0) {
        return price;
    }

    ///////////////////////////////////////////////////////////////////////// 숫자를 세 자리마다 콤마로 구분하여 문자열로 변환

    return priceToNumber.toLocaleString('en-US') + "원";
} 

/* formatDateKor */ 

export const formatDateKor = (dateString: string): string => { 

    ///////////////////////////////////////////////////////////////////////// 연도, 월, 일을 추출

    const year = dateString.slice(0, 4);
    const month = dateString.slice(4, 6);
    const day = dateString.slice(6, 8);
  
    ///////////////////////////////////////////////////////////////////////// 원하는 형식으로 변환

    return `${year}.${month}.${day}`;
}; 

/* formatDateTimeKor */

export const formatDateTimeKor = (dateString: string | null | undefined): string => {
    if (!dateString) return "-";

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "-";

    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const hour = date.getHours();
    const minute = date.getMinutes();

    return `${year}년 ${month}월 ${day}일 ${hour}시 ${minute}분`;
};

/* formatDate */ 

export const formatDate = (date: Date): string => { 
    const formattedDate = date.toISOString().split('T')[0]; // 'YYYY-MM-DD' 형식으로 변환
    return formattedDate;
}; 

/* formatRelativeTime */

export const formatRelativeTime = (date: Date | string): string => {
    const target = typeof date === "string" ? new Date(date) : date;
    return formatDistanceToNow(target, { addSuffix: true, locale: ko });
};

/* formatExpiryDate */ 

export const formatExpiryDate = (dateString: string): string => {
    
    const year = dateString.slice(0, 2);
    const month = dateString.slice(2, 4);
    const day = dateString.slice(4, 6);

    return `${year}년 ${month}월 ${day}일`;

};

/* 기간 변수 */

export const NOW = formatDate(new Date());
export const ONE_MONTH_AGO = formatDate(new Date(new Date().setMonth(new Date().getMonth() - 1)));
export const THREE_MONTHS_AGO = formatDate(new Date(new Date().setMonth(new Date().getMonth() - 3)));
export const SIX_MONTHS_AGO = formatDate(new Date(new Date().setMonth(new Date().getMonth() - 6)));
export const NINE_MONTHS_AGO = formatDate(new Date(new Date().setMonth(new Date().getMonth() - 9)));
export const ONE_YEAR_AGO = formatDate(new Date(new Date().setFullYear(new Date().getFullYear() - 1))); 

/* formatMonthLabel */

export const formatMonthLabel = (key: string) => {
    const [year, month] = key.split("-");
    if (year && month) return `${year}년 ${month}월`;
    return key;
}; 

/* dowLabels */ 

export const dowLabels = ["일", "월", "화", "수", "목", "금", "토"];


/* intentToKor */

export const intentToKor = (intent: Intent) => {
    switch (intent) {
        case "BASIC_SEARCH":
            return "기본 검색";
        
        case "EFFICACY_SEARCH":
            return "효능을 기준으로 검색한 경우";
        
        case "SAME_INGREDIENT_BY_PRODUCT":
            return "특정 제품과 성분이 동일한 제품을 찾은 경우";
        
        case "SAME_INGREDIENT_AND_AMOUNT_BY_PRODUCT":
            return "특정 제품과 성분, 함량이 모두 같은 제품을 찾은 경우";
        
        case "SIMILAR_PRODUCT_BY_PRODUCT":
            return "특정 제품과 유사한 제품을 찾은 경우";
        
        case "SIMILAR_PRODUCT_CHEAPER_BY_PRODUCT":
            return "유사한 제품 중 저렴한 제품을 찾은 경우";
        
        case "SIMILAR_PRODUCT_AVOID_INGREDIENTS_BY_PRODUCT":
            return "특정 성분은 뺀, 유사한 제품을 찾은 경우";
        
        case "EFFICACY_EXCLUDE_INGREDIENT":
            return "효능은 같으면서 특정 성분을 제외한 경우";
        
        case "INGREDIENT_WITH_FORM":
            return "특정 성분과 제형(형태)을 같이 고려한 경우";
        
        case "INGREDIENT_WITH_FORM_AND_PACKAGE_QTY":
            return "특정 성분, 제형, 포장 양을 함께 고려한 경우";
        
        case "INGREDIENT_WITH_FORM_CHEAPER":
            return "특정 성분과 제형을 보면서 저렴한 제품을 찾은 경우";
        
        case "INGREDIENT_WITH_FORM_AND_PACKAGE_QTY_CHEAPER":
            return "특정 성분/제형/포장 양을 맞추고 저렴한 제품을 찾은 경우";
        
        case "INGREDIENT_WITH_STRENGTH":
            return "특정 성분의 함량(세기)을 같이 본 경우";
        
        case "EFFICACY_WITH_FORM":
            return "효능과 제형을 함께 본 경우";
        
        case "EFFICACY_WITH_DOSE_FREQUENCY":
            return "효능과 복용 주기를 같이 고려한 경우";
        
        case "EFFICACY_WITH_EASY_TO_TAKE":
            return "먹기 쉬운지까지 본 경우";
        
        case "EFFICACY_WITH_USE_METHOD":
            return "효능과 사용 방법을 같이 본 경우";
        
        case "EFFICACY_WITH_LOCAL_USE":
            return "효능과 국소 사용을 같이 본 경우";
        
        case "EFFICACY_WITH_PACKAGE_QTY":
            return "포장 양 조건을 추가한 경우";
        
        case "EFFICACY_WITH_PACKAGE_FORM":
            return "포장 형태까지 조건에 넣은 경우";
        
        case "EFFICACY_WITH_PACKAGE_TEXT":
            return "포장에 쓰인 문구도 조건에 포함한 경우";
        
        case "EFFICACY_WITH_PRICE_UNDER":
            return "특정 가격 이하로 제한한 경우";
        
        case "EFFICACY_CHEAPER":
            return "효능은 같고 가격이 더 저렴한 제품을 찾은 경우";
        
        case "EFFICACY_WITH_PACKAGE_CONDITION_CHEAPER":
            return "포장 조건과 더불어 저렴한 제품을 찾은 경우";
        
        case "EFFICACY_FOR_PREGNANT_OR_BREASTFEEDING":
            return "임신/수유 중에도 사용 가능한 제품인 경우";
        
        case "EFFICACY_WITH_DISEASE_CAUTION":
            return "질병으로 인한 주의 사항이 포함된 경우";
        
        case "EFFICACY_LOW_STOMACH_IRRITATION":
            return "위 자극이 적은 제품을 찾은 경우";
        
        case "EFFICACY_WITH_ENVIRONMENT_CONDITION":
            return "환경 조건(예: 계절 등)을 함께 고려한 경우";
        
        case "EFFICACY_STRONG_EFFECT":
            return "특히 효과가 강한 제품을 찾은 경우";
        
        case "EFFICACY_POPULAR":
            return "많이 찾는 인기 제품인 경우";
        
        case "EFFICACY_WITH_AGE":
            return "특정 연령 조건을 넣은 경우";
        
        case "EFFICACY_WITH_FORM_AND_AGE":
            return "형태와 나이까지 모두 본 경우";
        
        case "SET_RECOMMENDATION":
            return "추천 제품인 경우";
        
        case "EFFICACY_SET_RECOMMENDATION":
            return "효능에 맞춘 추천 제품인 경우";
        
        case "SEASONAL_RECOMMENDATION":
            return "계절별로 추천하는 경우";
        
        case "UNKNOWN":
            return "알 수 없음";
    }
}

/* noteToTag */

const NOTE_TAG_BACKGROUND_COLOR_MAP: Record<string, string> = {
    순: colors.smooth,
    핫: colors.apple,
    쿨: colors.light,
    쿨하이: colors.covalt,
    핫앤쿨: colors.link,
};

export const NOTE_TAG_LABEL_COLOR_MAP: Record<string, string> = {
    순: colors.white,
    핫: colors.white,
    쿨: colors.blue,
    쿨하이: colors.white,
    핫앤쿨: colors.white,
};

export const noteToTags = (note?: string | null): string[] => {
    if (!note) return [];

    return note
        .split("#")
        .map((tag) => tag.trim())
        .filter(Boolean);
};

export const noteTagToBackgroundColor = (tag: string): string => {
    return NOTE_TAG_BACKGROUND_COLOR_MAP[tag] ?? colors.g5;
};

export const noteTagToLabelColor = (tag: string): string => {
    return NOTE_TAG_LABEL_COLOR_MAP[tag] ?? colors.black;
};

/* typeToKor */

export const typeToKor = (type: "OWNER" | "MEMBER" | "PHARMACIST" | "STUDENT" | "GUEST" | null) => {
    switch (type) {
        case "OWNER":
            return "개국약사";
        case "MEMBER":
            return "멤버";
        case "PHARMACIST":
            return "약사";
        case "STUDENT":
            return "약대생";
        case "GUEST":
            return "비회원";
        default:
            return "";
    }
}

/* statusToKor */

export const statusToKor = (status: "ACTIVE" | "PENDING" | "EDITED" | "DENIED" | "REJECTED" | "SUSPENDED" | null) => {
    switch (status) {
        case "ACTIVE":
            return "승인";
        case "PENDING":
            return "검토 중";
        case "EDITED":
            return "수정 검토 중";
        case "DENIED":
            return "거절됨";
        case "REJECTED":
            return "반려됨";
        case "SUSPENDED":
            return "정지";
        default:
            return "";
    }
}